import React from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";

/**
 * Parallax glow that reacts to both scroll and pointer position.
 * Uses motion springs for smooth cinematic drift.
 */
export const ParallaxGlow = ({
    className = "",
    color = "violet", // "violet" | "cyan" | "white"
    size = 600,
    intensity = 1,
    parallaxStrength = 40,
    pointerStrength = 30,
    style = {},
}) => {
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [-parallaxStrength, parallaxStrength]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 40, damping: 16 });
    const sy = useSpring(my, { stiffness: 40, damping: 16 });

    React.useEffect(() => {
        const onMove = (e) => {
            const nx = (e.clientX / window.innerWidth - 0.5) * pointerStrength;
            const ny = (e.clientY / window.innerHeight - 0.5) * pointerStrength;
            mx.set(nx);
            my.set(ny);
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        return () => window.removeEventListener("mousemove", onMove);
    }, [mx, my, pointerStrength]);

    const palette = {
        violet: "rgba(110, 60, 255, 0.55)",
        cyan: "rgba(0, 229, 255, 0.40)",
        white: "rgba(255, 255, 255, 0.35)",
    }[color];

    return (
        <motion.div
            aria-hidden="true"
            className={`pointer-events-none absolute ${className}`}
            style={{
                width: size,
                height: size,
                x: sx,
                y,
                ...style,
            }}
        >
            <motion.div
                style={{ x: 0, y: sy }}
                className="w-full h-full"
            >
                <div
                    className="w-full h-full"
                    style={{
                        background: `radial-gradient(closest-side, ${palette}, rgba(0,0,0,0) 75%)`,
                        filter: `blur(${70 * intensity}px)`,
                        opacity: intensity,
                    }}
                />
            </motion.div>
        </motion.div>
    );
};

export default ParallaxGlow;
